import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const THEMES = ['system', 'light', 'dark'];

// Resolve a theme choice to the boolean `nightMode` the rest of the app reads.
// App.js owns the live `matchMedia` listener for 'system'; this only gives an
// immediate, sensible value at the moment the user changes the setting.
function resolveNightMode(theme) {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  try {
    return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export const useAppStore = create(
  persist(
    (set, get) => ({
      selectedBabyId: null,
      nightMode: false,
      // 'system' | 'light' | 'dark' — App.js resolves 'system' via matchMedia.
      theme: 'system',
      user: null,

      // Persistent timer state — FEED_DUAL / PUMPING / ACTIVITY only. SLEEP no
      // longer uses this: the open SLEEP event is the timer (features/sleep/useOpenSleep).
      activeTimer: null, // { type, startedAt, meta }
      // Milestone celebrations
      dismissedCelebrations: [],
      // Handoff tracking: `lastActiveAt` is bumped on every launch, so the
      // dashboard handoff compares against `previousActiveAt` (the value it
      // held BEFORE this launch's bump).
      lastActiveAt: Date.now(),
      previousActiveAt: null,

      setSelectedBabyId: (id) => set({ selectedBabyId: id }),
      setUser: (user) => set({ user }),
      toggleNightMode: () => set((s) => ({ nightMode: !s.nightMode })),
      setNightMode: (value) => set({ nightMode: value }),
      setTheme: (value) => {
        const theme = THEMES.includes(value) ? value : 'system';
        set({ theme, nightMode: resolveNightMode(theme) });
      },

      // Timer actions
      startTimer: (type, meta = {}, startedAt = Date.now()) =>
        set({ activeTimer: { type, startedAt, meta } }),
      stopTimer: () => set({ activeTimer: null }),

      // Celebrations
      dismissCelebration: (key) =>
        set((s) => ({
          dismissedCelebrations: [...s.dismissedCelebrations, key],
        })),

      // Handoff
      updateLastActiveAt: () =>
        set({ previousActiveAt: get().lastActiveAt, lastActiveAt: Date.now() }),
    }),
    {
      name: 'babytrack-app',
      version: 1,
      // v0 → v1: there was no `theme`, only the `nightMode` boolean the Night
      // Mode switch wrote. App.js now resolves 'system' from matchMedia ON
      // MOUNT, so an upgrading user left on the default 'system' would have
      // their persisted Night Mode overwritten by the OS setting at every
      // launch. Carry a switched-on Night Mode over as an explicit 'dark'.
      migrate: (persisted, version) => {
        if (!persisted || typeof persisted !== 'object') return persisted;
        if (version < 1 && persisted.nightMode === true && (persisted.theme || 'system') === 'system') {
          return { ...persisted, theme: 'dark' };
        }
        return persisted;
      },
      partialize: (state) => ({
        selectedBabyId: state.selectedBabyId,
        nightMode: state.nightMode,
        theme: state.theme,
        activeTimer: state.activeTimer,
        dismissedCelebrations: state.dismissedCelebrations,
        lastActiveAt: state.lastActiveAt,
        previousActiveAt: state.previousActiveAt,
      }),
    }
  )
);
